import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { AppContext } from "../../context";
import { ArrowLeft, CircleAlert, Info, MoreVertical } from "lucide-react";
import {
  getStoreProjectItems,
  getStoreProjectSummary,
  createStoreProjectItem,
  updateStoreProjectItem,
  deleteStoreProjectItem,
  getSignedToken,
  updateStoreProjectStatus,
  updateStoreProject,
  duplicateStoreProject,
  copyStoreProjectToUser,
  destroyStoreProject,
  refreshStoreProjectQuotePrices,
  resetStoreProjectProformaInvoice,
  shouldPromptStoreProjectNameOnPlace,
} from "../service";
import StoreProjectNameModal from "../components/StoreProjectNameModal";
import {
  formatStoreOrderLabel,
  formatBuyerFirstUserLine,
} from "./storeSalesDashboardHelpers";
import { BuyerDashboardNameCell } from "./StoreSalesDashboardBuyersSection";
import { searchStoreBuyerUsers } from "../sales/service";
import {
  isBuyerBillingCompleteForDocument,
  isBuyerProfileCompleteForPlaceOrder,
} from "./storeDocumentHelpers";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import "./StoreProjectDetail.css";
import "../products.css";
import ProductDetailModal from "../../catalog/ProductDetailModal";
import {
  projectItemsBodyToTreeAndFlat,
  cloneProjectItemsTree,
  flattenProjectItemsTree,
  applyOptimisticCartReorder,
  applyOptimisticCartMoveToParent,
  getProjectItemsProjectStatus,
  getProjectItemsActions,
  getProjectHasDealer,
  getProjectMode,
  getProjectDealerOrder,
  getProjectItemsCanManageProjectItems,
  getProjectItemsSample,
  quoteHasArchivedProducts,
} from "../projectItemsTree";
import {
  collectFolderIdsFromTree,
  getProjectItemProductId,
  projectCartCollisionDetection,
  isTouchUi,
  toPriceNum,
  formatInr,
  getProjectItemDisplayName,
} from "./storeProjectDetail/cartUtils";
import {
  CartCloneFlashProvider,
  ProjectItemTree,
  useCartCloneFlash,
} from "./storeProjectDetail/ProjectCartTree";
import StoreQuoteFolderPickerModal from "../components/StoreQuoteFolderPickerModal";
import ProjectRootDropZone from "./storeProjectDetail/ProjectRootDropZone";
import StoreProjectPriceSummary, {
  storeAmountInclGst,
} from "../components/StoreProjectPriceSummary";
import DealerOrderReviewModal from "../components/DealerOrderReviewModal";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  UncontrolledTooltip,
  Alert,
} from "reactstrap";
import { generateRequestUri } from "../../common/service";
import { trackZbInitiateCheckout } from "../../common/analytics";
import {
  resolveStoreProjectActionDock,
  isInteractiveProjectDockClickable,
} from "./storeProjectDetail/projectActionDock";
import { isZerobuildDomain } from "../../utils";

const getApiErrorMessage = (error, fallback) => {
  const raw = error?.response?.data?.error;
  if (typeof raw === "string" && raw.trim() !== "") return raw;
  if (
    raw &&
    typeof raw === "object" &&
    typeof raw.message === "string" &&
    raw.message.trim() !== ""
  ) {
    return raw.message;
  }
  const msg = error?.response?.data?.message;
  if (typeof msg === "string" && msg.trim() !== "") return msg;
  if (typeof error?.message === "string" && error.message.trim() !== "") {
    return error.message;
  }
  return fallback;
};

/** Walk cart `itemTree` for DragOverlay / labels while dragging. */
function findStoreProjectCartTreeItemById(nodes, id) {
  const want = Number(id);
  if (!Number.isFinite(want)) return null;
  const list = nodes ?? [];
  for (const n of list) {
    if (Number(n?.id) === want) return n;
    if (n?.children?.length) {
      const found = findStoreProjectCartTreeItemById(n.children, id);
      if (found) return found;
    }
  }
  return null;
}

function ProjectCartDuplicateFolderPicker({
  duplicateFolderPicker,
  onClose,
  projectId,
  projectName,
  onRefresh,
}) {
  const { flashCloneItem } = useCartCloneFlash();

  return (
    <StoreQuoteFolderPickerModal
      isOpen={duplicateFolderPicker.isOpen}
      toggle={onClose}
      projectId={projectId}
      projectName={projectName}
      mode="duplicate"
      duplicateSourceItem={duplicateFolderPicker.item}
      onAdded={({ clonedItemId }) => {
        onRefresh();
        if (clonedItemId != null) {
          requestAnimationFrame(() => {
            flashCloneItem?.(clonedItemId);
          });
        }
        onClose();
      }}
    />
  );
}

function StoreProjectHeaderTitleSkeleton() {
  return (
    <span
      aria-hidden
      className="placeholder placeholder-wave rounded d-inline-block"
      style={{ width: "8rem", height: "1.125rem", maxWidth: "60vw" }}
    />
  );
}

function StoreProjectHeaderPriceSkeleton() {
  return (
    <span
      aria-hidden
      className="placeholder placeholder-wave rounded d-inline-block"
      style={{ width: "5.5rem", height: "1.125rem" }}
    />
  );
}

function StoreProjectCartLoadingSkeleton({ rows = 6 }) {
  return (
    <div className="px-2 py-2" aria-live="polite" aria-busy="true">
      {Array.from({ length: rows }, (_, idx) => (
        <div
          key={`project-cart-skeleton-${idx}`}
          className="store-products-row store-project-cart-line d-flex align-items-start flex-wrap"
        >
          <div className="store-products-row__media-col d-flex align-items-center">
            <div
              aria-hidden
              className="placeholder placeholder-wave rounded flex-shrink-0"
              style={{ width: "40px", height: "52px" }}
            />
          </div>
          <div className="store-products-row__detail-col flex-grow-1 min-w-0">
            <div className="store-products-row__detail-stack placeholder-glow d-flex flex-column gap-2">
              <span
                className="placeholder rounded"
                style={{ width: "70%", height: "0.875rem", maxWidth: "14rem" }}
              />
              <span
                className="placeholder rounded"
                style={{ width: "42%", height: "0.7rem", maxWidth: "9rem" }}
              />
              <span
                className="placeholder rounded"
                style={{ width: "54%", height: "0.78rem", maxWidth: "10rem" }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const StoreProjectDetail = () => {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [projectMeta, setProjectMeta] = useState(() => {
    const initial = location.state?.project;
    return initial && typeof initial === "object" ? initial : null;
  });
  const [projectName, setProjectName] = useState(() => {
    const initial = location.state?.project?.name;
    return typeof initial === "string" ? initial : "";
  });
  const {
    currentUser: rawCurrentUser,
    openAuthModal,
    openProfileModal,
    isSessionLoaded,
    storeProjectsList = [],
    setStoreProjectsList,
  } = useContext(AppContext) || {};
  const currentUser =
    rawCurrentUser &&
    typeof rawCurrentUser === "object" &&
    !Array.isArray(rawCurrentUser)
      ? rawCurrentUser
      : null;
  const isTempGuestUser =
    Boolean(currentUser?.temp === true) ||
    String(currentUser?.mobile ?? "")
      .toLowerCase()
      .startsWith("guest_");
  const isLoggedIn = Boolean(currentUser) && !isTempGuestUser;
  const canShowPrice = isLoggedIn;
  const showLoginToViewPrice = Boolean(isSessionLoaded) && !isLoggedIn;
  const storeViewerRole = useMemo(() => {
    if (currentUser?.admin) return "admin";
    if (currentUser?.dealer) return "dealer";
    return "buyer";
  }, [currentUser?.admin, currentUser?.dealer]);
  const canShowPriceForViewer = canShowPrice;
  const [isSampleQuote, setIsSampleQuote] = useState(
    () => location.state?.project?.sample === true
  );
  const [isProjectFrozen, setIsProjectFrozen] = useState(
    () => location.state?.project?.frozen === true
  );
  const canEditProjectCart = !isSampleQuote && !isProjectFrozen;
  const [canManageProjectItems, setCanManageProjectItems] = useState(
    () => location.state?.project?.can_manage_project_items === true
  );
  const [items, setItems] = useState([]);
  const [itemTreeData, setItemTreeData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [folderParentId, setFolderParentId] = useState(null);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [draggingItemId, setDraggingItemId] = useState(null);
  const [isDraggingItem, setIsDraggingItem] = useState(false);
  /** `over.id` while reorder-dragging — drives “gap” before the hovered `ord-*` row. */
  const [cartReorderActiveDropId, setCartReorderActiveDropId] = useState(null);
  /** Touch UI: which row shows move + ⋮ after user tapped the row (laptop uses hover only). */
  const [mobileActionsItemId, setMobileActionsItemId] = useState(null);
  /** Which cart folders are expanded — same Set pattern as `src/store/products.jsx`. */
  const [openProjectFolderIds, setOpenProjectFolderIds] = useState(
    () => new Set()
  );
  const [productDetailModal, setProductDetailModal] = useState(null);
  const prevProjectFolderTreeSigRef = useRef("");
  const cartPanelRef = useRef(null);
  const projectActionsRef = useRef(null);
  const qtyActionLockRef = useRef(false);
  const [showProjectActionsMenu, setShowProjectActionsMenu] = useState(false);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [isRefreshingQuotePrices, setIsRefreshingQuotePrices] = useState(false);
  const [showPlaceOrderConfirm, setShowPlaceOrderConfirm] = useState(false);
  const [pendingPlaceOrderConfirmMode, setPendingPlaceOrderConfirmMode] =
    useState(null);
  const [placeOrderNameModal, setPlaceOrderNameModal] = useState({
    isOpen: false,
    draftName: "",
    saving: false,
    error: null,
    pendingPlacementMode: null,
  });
  const [isPriceSummaryOpen, setIsPriceSummaryOpen] = useState(false);
  const [showBackToDraftConfirm, setShowBackToDraftConfirm] = useState(false);
  const [renameModal, setRenameModal] = useState({
    isOpen: false,
    draftName: "",
    originalName: "",
    saving: false,
    error: null,
  });
  const [archiveModal, setArchiveModal] = useState({
    isOpen: false,
    saving: false,
    error: null,
  });
  const [duplicateModal, setDuplicateModal] = useState({
    isOpen: false,
    saving: false,
    error: null,
  });
  const [duplicateFolderPicker, setDuplicateFolderPicker] = useState({
    isOpen: false,
    item: null,
  });
  const [copyProjectModal, setCopyProjectModal] = useState({
    isOpen: false,
    mobile: "",
    searching: false,
    searchError: null,
    results: [],
    selectedBuyerUserId: null,
    saving: false,
    error: null,
  });
  const [showPlacedWithoutDealerInfo, setShowPlacedWithoutDealerInfo] =
    useState(false);
  const [isShipmentItemsModalOpen, setIsShipmentItemsModalOpen] =
    useState(false);
  const [selectedShipmentIndex, setSelectedShipmentIndex] = useState(0);
  const [projectHasDealer, setProjectHasDealer] = useState(undefined);
  const [projectShipments, setProjectShipments] = useState([]);
  const [projectStatus, setProjectStatus] = useState(null);
  const [projectMode, setProjectMode] = useState(null);
  const [dealerOrder, setDealerOrder] = useState(null);
  const [dealerAcceptingOrder, setDealerAcceptingOrder] = useState(false);
  const [dealerReviewModal, setDealerReviewModal] = useState({
    isOpen: false,
    showAcceptButton: false,
  });
  const [billingDetailsModalOpen, setBillingDetailsModalOpen] = useState(false);
  const [projectActions, setProjectActions] = useState({
    nextStatus: null,
    access: false,
  });
  const [canBackToDraft, setCanBackToDraft] = useState(false);
  /** Items total incl. GST (excludes delivery); synced from Order Summary. */
  const [headerPayableTotal, setHeaderPayableTotal] = useState(null);
  const [isHeaderPayableLoading, setIsHeaderPayableLoading] = useState(false);
  const checkoutTrackedRef = useRef(false);
  const backPath = currentUser?.admin
    ? "/admin/dashboard"
    : currentUser?.customer === true
    ? "/projects"
    : "/";
  const isDraftProject = projectStatus === "draft";
  const proformaInvoiceEligible =
    !isDraftProject &&
    projectStatus !== "rejected" &&
    [
      "placed",
      "dealer_accepted",
      "accepted",
      "dispatched",
      "partially_delivered",
      "delivered",
    ].includes(projectStatus);

  const fetchHeaderPayableFromSummary = useCallback(async () => {
    if (!projectId) return null;
    try {
      const body = await getStoreProjectSummary(projectId);
      return storeAmountInclGst(body?.data?.items_price);
    } catch {
      return null;
    }
  }, [projectId]);

  const refreshHeaderPayableTotal = useCallback(async () => {
    if (!projectId || !canShowPriceForViewer) {
      setHeaderPayableTotal(null);
      return;
    }
    if (storeViewerRole === "dealer") {
      const payable = Number(dealerOrder?.payable_total ?? dealerOrder?.value);
      setHeaderPayableTotal(
        Number.isFinite(payable) && payable > 0 ? payable : null
      );
      return;
    }
    const itemsWithGst = await fetchHeaderPayableFromSummary();
    setHeaderPayableTotal(itemsWithGst);
  }, [
    projectId,
    canShowPriceForViewer,
    storeViewerRole,
    dealerOrder,
    fetchHeaderPayableFromSummary,
  ]);

  const handleSummaryPayableHeaderSync = useCallback((n) => {
    if (typeof n === "number" && Number.isFinite(n)) {
      setHeaderPayableTotal(n);
    }
  }, []);
  const isAcceptedProject = [
    "accepted",
    "dispatched",
    "partially_delivered",
    "delivered",
  ].includes(projectStatus);
  const packingListEligible = [
    "dispatched",
    "partially_delivered",
    "delivered",
  ].includes(projectStatus);
  const usePersistedItemPrices = true;
  const quotePricesStale = projectMeta?.quote_prices_stale === true;
  const showStaleQuotePrices =
    quotePricesStale && canManageProjectItems === true;
  /** Same gate as StoreProjectPriceSummary place-order buttons. */
  const staleBlocksPlace = showStaleQuotePrices;
  const quoteHasArchivedLineItems = useMemo(
    () => quoteHasArchivedProducts(items),
    [items]
  );
  const archivedBlocksPlace = isDraftProject && quoteHasArchivedLineItems;
  const canMutateProjectCart =
    canEditProjectCart && isDraftProject && canManageProjectItems;
  const cartReorderDndActive = canMutateProjectCart;
  const headerTitle =
    formatStoreOrderLabel({
      name: projectName,
      order_identifier: projectMeta?.order_identifier,
    }) || "Quote";
  const showHeaderPriceSkeleton =
    canShowPriceForViewer &&
    headerPayableTotal == null &&
    (isHeaderPayableLoading || isLoading);
  const projectNextStatus = projectActions.nextStatus;
  const canRunProjectAction = projectActions.access === true;
  const shouldShowViewSummaryButton =
    projectNextStatus === "placed" && canRunProjectAction;
  const placedWithoutDealer =
    projectStatus === "placed" && projectHasDealer === false;
  const projectActionDock = useMemo(() => {
    return resolveStoreProjectActionDock({
      viewerRole: storeViewerRole,
      status: projectStatus,
      placedWithoutDealer,
      isSampleQuote,
    });
  }, [storeViewerRole, projectStatus, placedWithoutDealer, isSampleQuote]);
  const buyerProfileComplete = isBuyerProfileCompleteForPlaceOrder(
    currentUser?.buyer
  );
  const profileBlocksPlace =
    storeViewerRole === "buyer" &&
    isDraftProject &&
    projectActionDock?.label === "Place Order" &&
    !buyerProfileComplete;
  const dockBuyerQuoteActionsAllowed =
    storeViewerRole !== "buyer" || canManageProjectItems;
  const dockActionClickable =
    projectActionDock != null &&
    isInteractiveProjectDockClickable(projectActionDock, canRunProjectAction) &&
    dockBuyerQuoteActionsAllowed &&
    (!profileBlocksPlace || shouldShowViewSummaryButton);

  /** Guest / price-gated buyer: Place Order prompts login instead of confirm. */
  const mustLoginBeforePlaceOrder =
    storeViewerRole === "buyer" &&
    isDraftProject &&
    projectActionDock?.variant === "button" &&
    !canShowPrice &&
    canManageProjectItems;
  const dockPlaceOrderInteractable =
    (dockActionClickable || mustLoginBeforePlaceOrder) &&
    !staleBlocksPlace &&
    !archivedBlocksPlace;
  /** Hide bottom project-action dock for view-only buyers (sample orders always show call/summary actions). */
  const hideBuyerProjectActionDock =
    storeViewerRole === "buyer" &&
    !isLoading &&
    isDraftProject &&
    !canManageProjectItems &&
    !isSampleQuote;

  const SAMPLE_ORDER_SUPPORT_PHONE = "9150469996";

  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );
  const useZbColumnOverlay = isZerobuildDomain() && !isMobile;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const projectListModalClassName = useZbColumnOverlay
    ? "position-fixed top-0 vh-100 bg-white d-flex flex-column overflow-hidden store-project-list-modal store-project-list-modal--zb-column"
    : "position-fixed top-0 start-0 vw-100 vh-100 bg-white d-flex flex-column overflow-hidden store-project-list-modal";

  const selectedShipment =
    projectShipments[selectedShipmentIndex] &&
    typeof projectShipments[selectedShipmentIndex] === "object"
      ? projectShipments[selectedShipmentIndex]
      : null;

  const selectedShipmentItems = Array.isArray(selectedShipment?.items)
    ? selectedShipment.items
    : [];

  const formatShipmentDate = useCallback((value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, []);

  const buildShipmentItemsTree = useCallback((items) => {
    const mkNode = (key, name) => ({
      key,
      name: name || "Folder",
      items: [],
      children: [],
      _childrenMap: new Map(),
    });

    const root = mkNode("root", "Root");
    const list = Array.isArray(items) ? items : [];

    list.forEach((item) => {
      const parents = Array.isArray(item?.parents) ? item.parents : [];
      let cursor = root;

      parents.forEach((parent, idx) => {
        const parentName =
          typeof parent?.name === "string" && parent.name.trim()
            ? parent.name.trim()
            : "Folder";
        const parentKeyRaw = parent?.id ?? `name:${parentName}`;
        const parentKey = `${String(parentKeyRaw)}:${idx}`;

        let child = cursor._childrenMap.get(parentKey);
        if (!child) {
          child = mkNode(parentKey, parentName);
          cursor._childrenMap.set(parentKey, child);
          cursor.children.push(child);
        }
        cursor = child;
      });

      cursor.items.push(item);
    });

    return root;
  }, []);

  const renderShipmentItemCard = useCallback((item, key) => {
    return (
      <div key={key} className="border rounded p-2">
        <div className="fw-semibold">{item?.name || "—"}</div>
        <div className="text-muted small">
          {item?.height ?? "—"} x {item?.width ?? "—"} x {item?.depth ?? "—"} -{" "}
          {item?.range ?? "—"}
        </div>
      </div>
    );
  }, []);

  const renderShipmentTree = useCallback(
    (node, depth = 0) => {
      const indentLevel = Math.max(depth - 1, 0);
      const indentPx = indentLevel * 12;
      const folderTitle =
        node.key === "root" ? null : (
          <div
            className="fw-semibold mb-1"
            style={{ marginLeft: `${indentPx}px` }}
          >
            {node.name || "Folder"}
          </div>
        );

      const itemRows = node.items.map((item, idx) =>
        renderShipmentItemCard(
          item,
          `${node.key}-item-${idx}-${item?.id ?? item?.name ?? "unknown"}`
        )
      );

      const childRows = node.children.map((child) => (
        <div key={child.key} className="mt-2">
          {renderShipmentTree(child, depth + 1)}
        </div>
      ));

      return (
        <div>
          {folderTitle}
          <div
            className="d-flex flex-column gap-2"
            style={{ marginLeft: node.key === "root" ? 0 : `${indentPx}px` }}
          >
            {itemRows}
            {childRows}
          </div>
        </div>
      );
    },
    [renderShipmentItemCard]
  );

  const toggleMobileActionsForItem = useCallback(
    (itemId) => {
      if (!canMutateProjectCart) return;
      if (!isTouchUi()) return;
      setMobileActionsItemId((prev) => (prev === itemId ? null : itemId));
    },
    [canMutateProjectCart]
  );

  useEffect(() => {
    const clearIfOutside = (e) => {
      const t = e.target;
      if (t?.closest?.(".dropdown-menu, [role='menu']")) return;
      if (cartPanelRef.current?.contains(t)) return;
      setMobileActionsItemId(null);
      if (!projectActionsRef.current?.contains(t)) {
        setShowProjectActionsMenu(false);
      }
    };
    document.addEventListener("touchstart", clearIfOutside, true);
    document.addEventListener("mousedown", clearIfOutside, true);
    return () => {
      document.removeEventListener("touchstart", clearIfOutside, true);
      document.removeEventListener("mousedown", clearIfOutside, true);
    };
  }, []);

  const downloadPackingList = () => {
    setShowProjectActionsMenu(false);
    getSignedToken({
      userId: currentUser.id,
      resource: "download_packing_list",
    }).then(({ token }) =>
      window.open(
        generateRequestUri({
          url: `/store/projects/${projectId}/packing_list`,
          params: { signed: token },
        }),
        "_blank",
        "noreferrer"
      )
    );
  };

  const openInstallation = useCallback(() => {
    setShowProjectActionsMenu(false);
    window.open(`/projects/${projectId}/installation`, "_blank", "noreferrer");
  }, [projectId]);

  const needsPlaceOrderProjectName = useMemo(
    () =>
      shouldPromptStoreProjectNameOnPlace({
        projects: storeProjectsList,
        projectId,
        projectName,
      }),
    [storeProjectsList, projectId, projectName]
  );

  const beginPlaceOrderFlow = useCallback(
    (placementMode = null) => {
      if (needsPlaceOrderProjectName) {
        setPlaceOrderNameModal({
          isOpen: true,
          draftName: "",
          saving: false,
          error: null,
          pendingPlacementMode: placementMode,
        });
        return;
      }
      if (placementMode != null && placementMode !== false) {
        setPendingPlaceOrderConfirmMode(placementMode);
        return;
      }
      setShowPlaceOrderConfirm(true);
    },
    [needsPlaceOrderProjectName]
  );

  const closePlaceOrderNameModal = useCallback(() => {
    setPlaceOrderNameModal((m) => {
      if (m.saving) return m;
      return {
        isOpen: false,
        draftName: "",
        saving: false,
        error: null,
        pendingPlacementMode: null,
      };
    });
  }, []);

  const handlePlaceOrderNameSubmit = useCallback(async () => {
    const trimmed = String(placeOrderNameModal.draftName ?? "").trim();
    if (!trimmed) {
      setPlaceOrderNameModal((m) => ({
        ...m,
        error: "Enter project name.",
      }));
      return;
    }

    setPlaceOrderNameModal((m) => ({ ...m, saving: true, error: null }));
    try {
      const res = await updateStoreProject(projectId, { name: trimmed });
      const updated = res?.data || res?.project || null;
      const newName = updated?.name ?? trimmed;
      setProjectName(newName);
      setProjectMeta((prev) =>
        prev && typeof prev === "object" ? { ...prev, name: newName } : prev
      );
      if (typeof setStoreProjectsList === "function") {
        setStoreProjectsList((prev) =>
          (Array.isArray(prev) ? prev : []).map((row) => {
            const rowId = Number(row?.id ?? row?.project_id);
            if (rowId !== Number(projectId)) return row;
            return { ...row, name: newName };
          })
        );
      }

      const placementMode = placeOrderNameModal.pendingPlacementMode;
      setPlaceOrderNameModal({
        isOpen: false,
        draftName: "",
        saving: false,
        error: null,
        pendingPlacementMode: null,
      });

      if (placementMode != null && placementMode !== false) {
        setPendingPlaceOrderConfirmMode(placementMode);
      } else {
        setShowPlaceOrderConfirm(true);
      }
    } catch (err) {
      setPlaceOrderNameModal((m) => ({
        ...m,
        saving: false,
        error: getApiErrorMessage(err, "Unable to save project name."),
      }));
    }
  }, [
    placeOrderNameModal.draftName,
    placeOrderNameModal.pendingPlacementMode,
    projectId,
    setStoreProjectsList,
  ]);

  const openRenameModal = () => {
    setShowProjectActionsMenu(false);
    setRenameModal({
      isOpen: true,
      draftName: projectName || "",
      originalName: projectName || "",
      saving: false,
      error: null,
    });
  };

  const closeRenameModal = () =>
    setRenameModal((m) => {
      if (m.saving) return m;
      return { ...m, isOpen: false, saving: false, error: null };
    });

  const handleRenameSubmit = async (e) => {
    e?.preventDefault?.();
    const trimmed = renameModal.draftName.trim();
    if (!trimmed) return;

    if (trimmed === renameModal.originalName.trim()) {
      setRenameModal((m) => ({ ...m, isOpen: false, error: null }));
      return;
    }

    setRenameModal((m) => ({ ...m, saving: true, error: null }));
    try {
      const res = await updateStoreProject(projectId, { name: trimmed });
      const updated = res?.data || res?.project || null;
      const newName = updated?.name ?? trimmed;
      setProjectName(newName);
      setProjectMeta((prev) =>
        prev && typeof prev === "object" ? { ...prev, name: newName } : prev
      );
      setRenameModal((m) => ({
        ...m,
        isOpen: false,
        saving: false,
        error: null,
      }));
    } catch (err) {
      setRenameModal((m) => ({
        ...m,
        saving: false,
        error: getApiErrorMessage(err, "Unable to rename quote."),
      }));
    }
  };

  const openDuplicateModal = () => {
    setShowProjectActionsMenu(false);
    setDuplicateModal({ isOpen: true, saving: false, error: null });
  };

  const closeDuplicateModal = () =>
    setDuplicateModal((m) => {
      if (m.saving) return m;
      return { ...m, isOpen: false, saving: false, error: null };
    });

  const handleDuplicateConfirm = async () => {
    setDuplicateModal((m) => ({ ...m, saving: true, error: null }));
    try {
      const res = await duplicateStoreProject(projectId);
      const newProject = res?.data ?? res;
      const newId = newProject?.id;
      setDuplicateModal({ isOpen: false, saving: false, error: null });
      if (newId != null) {
        window.open(`/projects/${newId}`, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      setDuplicateModal((m) => ({
        ...m,
        saving: false,
        error: getApiErrorMessage(err, "Unable to duplicate quote."),
      }));
    }
  };

  const openCopyProjectModal = () => {
    setShowProjectActionsMenu(false);
    setCopyProjectModal({
      isOpen: true,
      mobile: "",
      searching: false,
      searchError: null,
      results: [],
      selectedBuyerUserId: null,
      saving: false,
      error: null,
    });
  };

  const closeCopyProjectModal = () =>
    setCopyProjectModal((m) => {
      if (m.saving) return m;

      return {
        isOpen: false,
        mobile: "",
        searching: false,
        searchError: null,
        results: [],
        selectedBuyerUserId: null,
        saving: false,
        error: null,
      };
    });

  const handleCopyProjectMobileChange = (e) => {
    const digits = String(e.target.value || "")
      .replace(/\D/g, "")
      .slice(0, 10);

    setCopyProjectModal((m) => ({
      ...m,
      mobile: digits,
      searchError: null,
      results: [],
      selectedBuyerUserId: null,
      error: null,
    }));
  };

  const buyerUserSearchResultToRow = (result) => {
    const buyer = result.buyer;
    const buyerName = String(buyer?.name || "").trim();
    const dealerName = String(buyer?.dealer_name || "").trim();

    return {
      id: buyer?.id,
      buyerDisplayName: buyerName || "—",
      firstUserSubtitle: formatBuyerFirstUserLine({
        name: result.name,
        mobile: result.mobile,
      }),
      dealerName: dealerName || "-",
    };
  };

  const handleCopyProjectSearch = async () => {
    const mobile = copyProjectModal.mobile;
    if (!/^\d{10}$/.test(mobile)) return;

    setCopyProjectModal((m) => ({
      ...m,
      searching: true,
      searchError: null,
      results: [],
      selectedBuyerUserId: null,
      error: null,
    }));

    try {
      const res = await searchStoreBuyerUsers(mobile);
      const buyerUsers = res?.buyer_users;
      const results = Array.isArray(buyerUsers) ? buyerUsers : [];
      setCopyProjectModal((m) => ({
        ...m,
        searching: false,
        results,
        searchError:
          results.length === 0 ? "No buyer users found for this mobile." : null,
      }));
    } catch (err) {
      setCopyProjectModal((m) => ({
        ...m,
        searching: false,
        searchError: getApiErrorMessage(err, "Unable to search buyer users."),
      }));
    }
  };

  const handleCopyProjectConfirm = async () => {
    const targetUserId = copyProjectModal.selectedBuyerUserId;
    if (targetUserId == null) return;

    setCopyProjectModal((m) => ({ ...m, saving: true, error: null }));
    try {
      const res = await copyStoreProjectToUser(projectId, targetUserId);
      const newProject = res?.data ?? res;
      const newId = newProject?.id;
      setCopyProjectModal({
        isOpen: false,
        mobile: "",
        searching: false,
        searchError: null,
        results: [],
        selectedBuyerUserId: null,
        saving: false,
        error: null,
      });
      if (newId != null) {
        window.open(`/projects/${newId}`, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      setCopyProjectModal((m) => ({
        ...m,
        saving: false,
        error: getApiErrorMessage(err, "Unable to copy quote."),
      }));
    }
  };

  const openArchiveModal = () => {
    setShowProjectActionsMenu(false);
    setArchiveModal({ isOpen: true, saving: false, error: null });
  };

  const closeArchiveModal = () =>
    setArchiveModal((m) => {
      if (m.saving) return m;
      return { ...m, isOpen: false, saving: false, error: null };
    });

  const handleArchiveConfirm = async () => {
    setArchiveModal((m) => ({ ...m, saving: true, error: null }));
    try {
      await destroyStoreProject(projectId);
      setArchiveModal({ isOpen: false, saving: false, error: null });
      navigate(backPath);
    } catch (err) {
      setArchiveModal((m) => ({
        ...m,
        saving: false,
        error: getApiErrorMessage(err, "Unable to archive quote."),
      }));
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const loadItems = async (options = {}) => {
    const silent = Boolean(options.silent);
    const isDealer = storeViewerRole === "dealer";
    const shouldFetchSummary = canShowPriceForViewer && !isDealer;

    if (!silent) {
      setIsLoading(true);
      if (shouldFetchSummary) {
        setIsHeaderPayableLoading(true);
        setHeaderPayableTotal(null);
      }
    }

    const summaryPromise = shouldFetchSummary
      ? fetchHeaderPayableFromSummary()
      : null;

    setError(null);
    try {
      const body = await getStoreProjectItems(projectId);
      const apiProject = body?.data?.project;
      if (apiProject && typeof apiProject === "object") {
        setProjectMeta(apiProject);
        setIsSampleQuote(getProjectItemsSample(body));
        setIsProjectFrozen(apiProject?.frozen === true);
      }
      const nextName = body?.data?.project?.name;
      if (typeof nextName === "string" && nextName.trim() !== "") {
        setProjectName(nextName.trim());
      }
      setProjectStatus(getProjectItemsProjectStatus(body));
      setProjectMode(getProjectMode(body));
      const nextDealerOrder = getProjectDealerOrder(body);
      setDealerOrder(nextDealerOrder);
      setProjectHasDealer(getProjectHasDealer(body));
      const rawShipments = body?.data?.project?.shipments;
      setProjectShipments(Array.isArray(rawShipments) ? rawShipments : []);
      setProjectActions(getProjectItemsActions(body));
      setCanManageProjectItems(getProjectItemsCanManageProjectItems(body));
      setCanBackToDraft(body?.data?.project.can_move_back_to_draft);
      const { tree, flat } = projectItemsBodyToTreeAndFlat(body);
      setItemTreeData(tree);
      setItems(flat);
      if (isDealer) {
        const payable = Number(
          nextDealerOrder?.payable_total ?? nextDealerOrder?.value
        );
        setHeaderPayableTotal(
          Number.isFinite(payable) && payable > 0 ? payable : null
        );
      }
    } catch (e) {
      if (!silent && shouldFetchSummary) {
        setHeaderPayableTotal(null);
      }
      if (e?.response?.status === 404) {
        setError("Project not found or you no longer have access.");
      } else {
        setError(getApiErrorMessage(e, "Unable to load items."));
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }

    if (summaryPromise) {
      try {
        const itemsWithGst = await summaryPromise;
        if (!silent || itemsWithGst != null) {
          setHeaderPayableTotal(itemsWithGst);
        }
      } catch {
        if (!silent) {
          setHeaderPayableTotal(null);
        }
      } finally {
        if (!silent) {
          setIsHeaderPayableLoading(false);
        }
      }
    }
  };

  async function handleRefreshQuotePrices() {
    if (!projectId || isRefreshingQuotePrices) return;
    setIsRefreshingQuotePrices(true);
    setError(null);
    try {
      await refreshStoreProjectQuotePrices(projectId);
      await loadItems({ silent: true });
    } catch (e) {
      setError(getApiErrorMessage(e, "Unable to refresh quote prices."));
    } finally {
      setIsRefreshingQuotePrices(false);
    }
  }

  useEffect(() => {
    if (projectId) {
      loadItems({ silent: false });
    }
  }, [projectId, currentUser?.id]);

  useEffect(() => {
    setOpenProjectFolderIds(new Set());
    prevProjectFolderTreeSigRef.current = "";
  }, [projectId]);

  const toggleProjectFolder = useCallback((folderId) => {
    setOpenProjectFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  }, []);

  const openProductDetailFromLine = useCallback((item) => {
    const pid = getProjectItemProductId(item);
    if (pid == null) return;
    setProductDetailModal({
      openKey: `${Date.now()}`,
      productId: String(pid),
      initialAdded: Number(item?.added ?? 0),
      initialHasProjects: true,
      initialFolderPath: [],
    });
  }, []);

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!canMutateProjectCart) return;
    if (!folderName.trim()) return;

    setIsCreatingFolder(true);
    setError(null);
    try {
      await createStoreProjectItem(projectId, {
        item_type: "folder",
        name: folderName.trim(),
        parent_id: folderParentId || undefined,
      });
      setShowFolderModal(false);
      setFolderName("");
      setFolderParentId(null);
      loadItems({ silent: true });
    } catch (e) {
      setError(getApiErrorMessage(e, "Unable to create folder."));
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const handleAddFolder = (parentId = null) => {
    if (!canMutateProjectCart) return;
    setFolderParentId(parentId);
    setFolderName("");
    setShowFolderModal(true);
  };

  const itemTree = useMemo(() => {
    return cloneProjectItemsTree(itemTreeData);
  }, [itemTreeData]);

  const cartDragOverlaySourceItem = useMemo(() => {
    if (draggingItemId == null) return null;
    return findStoreProjectCartTreeItemById(itemTree, draggingItemId);
  }, [itemTree, draggingItemId]);

  const cartTotals = useMemo(() => {
    const visit = (nodes, out) => {
      if (!Array.isArray(nodes)) return;
      for (const n of nodes) {
        if (n?.item_type === "product") {
          out.push(n);
        }
        if (n?.children?.length) visit(n.children, out);
      }
    };

    const products = [];
    visit(itemTree, products);

    let listTotal = 0;
    let saleTotal = 0;
    let hasAnyPrice = false;

    for (const p of products) {
      const qty = 1;
      const listNum = toPriceNum(p?.price);
      const discNum = toPriceNum(p?.discount_price);
      if (usePersistedItemPrices && listNum == null && discNum == null) {
        // Line totals use cached columns only (no live listing fallback).
        continue;
      }
      const saleNum = discNum ?? listNum;
      const baseListNum = listNum ?? saleNum;
      if (saleNum == null && baseListNum == null) continue;
      hasAnyPrice = true;
      if (baseListNum != null) listTotal += baseListNum * qty;
      if (saleNum != null) saleTotal += saleNum * qty;
    }

    const discountTotal =
      hasAnyPrice && listTotal > saleTotal ? listTotal - saleTotal : 0;

    return {
      hasAnyPrice,
      listTotal,
      saleTotal,
      discountTotal,
    };
  }, [itemTree, usePersistedItemPrices]);

  useEffect(() => {
    if (checkoutTrackedRef.current) return;
    if (isLoading) return;
    checkoutTrackedRef.current = true;
    trackZbInitiateCheckout(Number(cartTotals.saleTotal) || 0);
  }, [isLoading, cartTotals.saleTotal]);

  useEffect(() => {
    const ids = collectFolderIdsFromTree(itemTree);
    const idSet = new Set(ids);
    const sorted = ids.slice().sort((a, b) => a - b);
    const sig = sorted.join(",");
    setOpenProjectFolderIds((prev) => {
      const next = new Set([...prev].filter((id) => idSet.has(id)));
      if (sig !== prevProjectFolderTreeSigRef.current) {
        const prevIds = new Set(
          prevProjectFolderTreeSigRef.current
            ? prevProjectFolderTreeSigRef.current.split(",").map(Number)
            : []
        );
        for (const id of ids) {
          if (!prevIds.has(id)) next.add(id);
        }
        prevProjectFolderTreeSigRef.current = sig;
      }
      return next;
    });
  }, [itemTree]);

  const handleNonCustomerProductDecrement = useCallback(
    async (item) => {
      if (!isDraftProject) return;
      const ids = [item.id];
      if (!ids.length) return;
      if (qtyActionLockRef.current) return;
      qtyActionLockRef.current = true;
      setError(null);
      try {
        const idToRemove = ids[ids.length - 1];
        await deleteStoreProjectItem(projectId, idToRemove);
        await loadItems({ silent: true });
      } catch (e) {
        setError(getApiErrorMessage(e, "Unable to remove item."));
      } finally {
        qtyActionLockRef.current = false;
      }
    },
    [projectId, isDraftProject]
  );

  const buildDescendantSet = (rootId) => {
    const descendants = new Set();
    const visit = (parentId) => {
      items
        .filter((it) => it.parent_id === parentId)
        .forEach((child) => {
          descendants.add(child.id);
          visit(child.id);
        });
    };
    visit(rootId);
    return descendants;
  };

  const moveItemToParent = async (dragItemId, targetParentId) => {
    if (!canMutateProjectCart) return;
    const dragItem = items.find((it) => it.id === dragItemId);
    if (!dragItem) return;
    if (dragItem.id === targetParentId) return;

    const currentParent =
      dragItem.parent_id === undefined || dragItem.parent_id === null
        ? null
        : dragItem.parent_id;
    const targetParent =
      targetParentId === undefined || targetParentId === null
        ? null
        : targetParentId;
    if (currentParent === targetParent) return;

    if (dragItem.item_type === "folder" && targetParentId != null) {
      const descendants = buildDescendantSet(dragItem.id);
      if (descendants.has(targetParentId)) {
        setError("Cannot move a folder inside its child.");
        return;
      }
    }

    const snapshot = cloneProjectItemsTree(itemTreeData);
    const insertAtChildIndex = targetParent == null ? undefined : 0;
    const nextTree = applyOptimisticCartMoveToParent(
      itemTreeData,
      dragItemId,
      targetParent,
      insertAtChildIndex
    );
    if (!nextTree) {
      setError("Unable to move item.");
      return;
    }

    const nextFlat = flattenProjectItemsTree(nextTree);
    const movedRow = nextFlat.find(
      (it) => Number(it.id) === Number(dragItemId)
    );
    const positionForApi =
      movedRow?.position === undefined || movedRow?.position === null
        ? undefined
        : Number(movedRow.position);

    setItemTreeData(nextTree);
    setItems(nextFlat);
    setError(null);

    try {
      await updateStoreProjectItem(projectId, dragItemId, {
        parent_id: targetParentId || null,
        ...(positionForApi !== undefined ? { position: positionForApi } : {}),
        name: dragItem.name || undefined,
      });
      await loadItems({ silent: true });
    } catch (e) {
      setItemTreeData(snapshot);
      setItems(flattenProjectItemsTree(snapshot));
      setError(getApiErrorMessage(e, "Unable to move item."));
    }
  };

  const moveItemToItemPosition = async (dragItemId, targetItemId) => {
    if (!canMutateProjectCart) return;
    const dragItem = items.find((it) => it.id === dragItemId);
    const targetItem = items.find((it) => it.id === targetItemId);
    if (!dragItem || !targetItem) return;
    if (dragItem.id === targetItem.id) return;

    const targetParentId =
      targetItem.parent_id === undefined || targetItem.parent_id === null
        ? null
        : targetItem.parent_id;
    const dragParentId =
      dragItem.parent_id === undefined || dragItem.parent_id === null
        ? null
        : dragItem.parent_id;

    if (dragItem.item_type === "folder") {
      const descendants = buildDescendantSet(dragItem.id);
      if (descendants.has(targetItem.id) || descendants.has(targetParentId)) {
        setError("Cannot move a folder inside its child.");
        return;
      }
    }

    const nextPosition =
      targetItem.position === undefined || targetItem.position === null
        ? undefined
        : targetItem.position;

    if (dragParentId === targetParentId && nextPosition === dragItem.position) {
      return;
    }

    const snapshot = cloneProjectItemsTree(itemTreeData);
    const nextTree = applyOptimisticCartReorder(
      itemTreeData,
      dragItemId,
      targetItemId
    );
    if (!nextTree) {
      setError("Unable to reorder item.");
      return;
    }

    const nextFlat = flattenProjectItemsTree(nextTree);
    const movedRow = nextFlat.find(
      (it) => Number(it.id) === Number(dragItemId)
    );
    const positionForApi =
      movedRow?.position === undefined || movedRow?.position === null
        ? undefined
        : Number(movedRow.position);

    setItemTreeData(nextTree);
    setItems(nextFlat);
    setError(null);

    try {
      await updateStoreProjectItem(projectId, dragItemId, {
        parent_id: targetParentId,
        position: positionForApi,
        name: dragItem.name || undefined,
      });
      await loadItems({ silent: true });
    } catch (e) {
      setItemTreeData(snapshot);
      setItems(flattenProjectItemsTree(snapshot));
      setError(getApiErrorMessage(e, "Unable to reorder item."));
    }
  };

  const handleDndDragMove = useCallback(
    (event) => {
      if (!cartReorderDndActive) return;
      setCartReorderActiveDropId(
        event.over?.id != null ? String(event.over.id) : null
      );
    },
    [cartReorderDndActive]
  );

  const handleDndDragStart = (event) => {
    if (!cartReorderDndActive) return;
    const id = String(event.active?.id ?? "");
    if (!id.startsWith("mv-")) return;
    const itemId = Number(id.slice(3));
    if (!itemId || Number.isNaN(itemId)) return;
    setDraggingItemId(itemId);
    setIsDraggingItem(true);
    setCartReorderActiveDropId(null);
  };

  const handleDndDragCancel = () => {
    if (!cartReorderDndActive) return;
    setIsDraggingItem(false);
    setDraggingItemId(null);
    setCartReorderActiveDropId(null);
  };

  const handleDndDragEnd = async (event) => {
    if (!cartReorderDndActive) return;
    const { active, over } = event;
    setCartReorderActiveDropId(null);
    setIsDraggingItem(false);
    setDraggingItemId(null);

    if (!over) return;

    const activeId = String(active.id);
    if (!activeId.startsWith("mv-")) return;
    const dragItemId = Number(activeId.slice(3));
    if (!dragItemId || Number.isNaN(dragItemId)) return;

    const overId = String(over.id);
    if (overId === "drop-root") {
      await moveItemToParent(dragItemId, null);
      return;
    }
    if (overId.startsWith("ord-")) {
      const targetItemId = Number(overId.slice(4));
      if (!targetItemId || Number.isNaN(targetItemId)) return;
      await moveItemToItemPosition(dragItemId, targetItemId);
      return;
    }
    if (overId.startsWith("fldh-")) {
      const folderId = Number(overId.slice(5));
      if (!folderId || Number.isNaN(folderId)) return;
      await moveItemToParent(dragItemId, folderId);
      return;
    }
    if (overId.startsWith("fld-")) {
      const folderId = Number(overId.slice(4));
      if (!folderId || Number.isNaN(folderId)) return;
      await moveItemToParent(dragItemId, folderId);
    }
  };

  const downloadArtifacts = () => {
    getSignedToken({
      userId: currentUser.id,
      resource: "download_artifacts",
    }).then(({ token }) =>
      window.open(
        generateRequestUri({
          url: `/store/projects/${projectId}/artifacts`,
          params: { signed: token },
        }),
        "_blank",
        "noreferrer"
      )
    );
  };

  const downloadEstimate = () => {
    setShowProjectActionsMenu(false);
    openDocumentDownload(`/projects/${projectId}/estimate`);
  };

  const downloadProformaInvoice = () => {
    setShowProjectActionsMenu(false);
    openDocumentDownload(`/projects/${projectId}/proforma_invoice`);
  };

  const buyerBillingForDocument = useMemo(() => {
    if (storeViewerRole === "buyer") {
      return currentUser?.buyer ?? null;
    }
    return projectMeta?.buyer_billing ?? null;
  }, [storeViewerRole, currentUser?.buyer, projectMeta?.buyer_billing]);

  const openDocumentDownload = (path) => {
    if (!isBuyerBillingCompleteForDocument(buyerBillingForDocument)) {
      setBillingDetailsModalOpen(true);
      return;
    }
    window.open(path, "_blank", "noreferrer");
  };

  const handleResetProformaInvoice = async () => {
    if (
      !window.confirm(
        "Reset proforma invoice number? A new PI number will be assigned on the next download."
      )
    ) {
      return;
    }
    setShowProjectActionsMenu(false);
    try {
      await resetStoreProjectProformaInvoice(projectId);
      setProjectMeta((prev) =>
        prev
          ? {
              ...prev,
              proforma_invoice_number: null,
              proforma_invoice_generated_at: null,
            }
          : prev
      );
    } catch (e) {
      setError(
        e?.response?.data?.error?.message ||
          e?.message ||
          "Unable to reset proforma invoice."
      );
    }
  };

  const downloadShutters = () => {
    getSignedToken({
      userId: currentUser.id,
      resource: "download_shutters",
    }).then(({ token }) =>
      window.open(
        generateRequestUri({
          url: `/store/projects/${projectId}/shutters`,
          params: { signed: token },
        }),
        "_blank",
        "noreferrer"
      )
    );
  };

  const handlePlaceOrder = useCallback(
    async (mode = null) => {
      if (!projectId || isPlacingOrder) return;
      if (staleBlocksPlace) return false;
      if (archivedBlocksPlace) return false;
      if (storeViewerRole === "buyer" && !canManageProjectItems) return;
      if (!projectNextStatus || !canRunProjectAction) return;
      const placementMode =
        mode ??
        projectMode ??
        (projectHasDealer === true ? "dealer" : "direct");
      const showNoDealerInfoAfter =
        projectNextStatus === "placed" &&
        (placementMode === "direct" || projectHasDealer === false);
      setError(null);
      setIsPlacingOrder(true);
      try {
        await updateStoreProjectStatus(
          projectId,
          projectNextStatus,
          placementMode
        );
        setProjectStatus(projectNextStatus);
        setShowPlaceOrderConfirm(false);
        await loadItems({ silent: true });
        if (showNoDealerInfoAfter) {
          setShowPlacedWithoutDealerInfo(true);
        }
        return true;
      } catch (e) {
        if (e?.response?.data?.error?.code === "AUTH_REQUIRED") {
          if (typeof openAuthModal === "function") {
            openAuthModal({
              returnTo: {
                pathname: `/projects/${projectId}`,
                ...(projectMeta ? { state: { project: projectMeta } } : {}),
              },
            });
            setShowPlaceOrderConfirm(false);
          }
          return;
        }
        setError(getApiErrorMessage(e, "Unable to update status."));
        return false;
      } finally {
        setIsPlacingOrder(false);
      }
    },
    [
      projectId,
      isPlacingOrder,
      storeViewerRole,
      canManageProjectItems,
      projectNextStatus,
      canRunProjectAction,
      projectHasDealer,
      projectMode,
      projectMeta,
      staleBlocksPlace,
      archivedBlocksPlace,
    ]
  );

  const openDealerReviewModal = useCallback(
    (row, { showAcceptButton = false } = {}) => {
      if (!row) return;
      setDealerReviewModal({ isOpen: true, showAcceptButton });
    },
    []
  );

  const closeDealerReviewModal = useCallback(() => {
    setDealerReviewModal({ isOpen: false, showAcceptButton: false });
  }, []);

  const handleDealerAcceptOrder = useCallback(async () => {
    const id = Number(projectId);
    if (!Number.isFinite(id) || id <= 0) return;
    if (dealerAcceptingOrder) return;
    setError(null);
    setDealerAcceptingOrder(true);
    try {
      await updateStoreProjectStatus(id, "dealer_accepted");
      closeDealerReviewModal();
      setIsPriceSummaryOpen(false);
      await loadItems({ silent: true });
    } catch (e) {
      setError(getApiErrorMessage(e, "Unable to accept order."));
    } finally {
      setDealerAcceptingOrder(false);
    }
  }, [projectId, dealerAcceptingOrder, closeDealerReviewModal, loadItems]);

  const handleDockActionClick = useCallback(() => {
    if (archivedBlocksPlace) return;
    if (!isPlacingOrder && mustLoginBeforePlaceOrder) {
      if (typeof openAuthModal === "function") {
        openAuthModal({
          returnTo: {
            pathname: `/projects/${projectId}`,
            ...(projectMeta ? { state: { project: projectMeta } } : {}),
          },
        });
      }
      return;
    }
    if (
      storeViewerRole === "dealer" &&
      projectActionDock?.label === "Review order"
    ) {
      if (!isPlacingOrder) setIsPriceSummaryOpen(true);
      return;
    }
    if (shouldShowViewSummaryButton && !isPlacingOrder) {
      setIsPriceSummaryOpen(true);
      return;
    }
    if (dockActionClickable && !isPlacingOrder) {
      beginPlaceOrderFlow(null);
    }
  }, [
    archivedBlocksPlace,
    beginPlaceOrderFlow,
    isPlacingOrder,
    mustLoginBeforePlaceOrder,
    storeViewerRole,
    projectActionDock?.label,
    shouldShowViewSummaryButton,
    dockActionClickable,
    openAuthModal,
    projectId,
    projectMeta,
  ]);

  const handleDockActionKeyDown = useCallback(
    (e) => {
      if (!dockPlaceOrderInteractable) return;
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      handleDockActionClick();
    },
    [dockPlaceOrderInteractable, handleDockActionClick]
  );

  const handleHeaderPriceSummaryClick = useCallback(() => {
    if (isPlacingOrder || archivedBlocksPlace) return;
    setIsPriceSummaryOpen(true);
  }, [isPlacingOrder, archivedBlocksPlace]);

  const handleBackToDraft = useCallback(async () => {
    if (!projectId || isPlacingOrder) return;

    setError(null);
    setIsPlacingOrder(true);

    try {
      await updateStoreProjectStatus(projectId, "draft");
      setShowBackToDraftConfirm(false);
      await loadItems({ silent: true });
    } catch (e) {
      setError(
        e?.response?.data?.error || e?.message || "Unable to update status."
      );
    } finally {
      setIsPlacingOrder(false);
    }
  }, [projectId, isPlacingOrder]);

  const handleOpenShipmentItemsModal = useCallback(
    (shipmentIndex) => {
      if (!Number.isInteger(shipmentIndex)) return;
      if (shipmentIndex < 0 || shipmentIndex >= projectShipments.length) return;
      setSelectedShipmentIndex(shipmentIndex);
      setIsShipmentItemsModalOpen(true);
    },
    [projectShipments.length]
  );

  const handleShipmentCardKeyDown = useCallback(
    (e, shipmentIndex) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      handleOpenShipmentItemsModal(shipmentIndex);
    },
    [handleOpenShipmentItemsModal]
  );

  return (
    <div className="container-fluid py-2">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <div className="d-flex align-items-center gap-1 mb-1">
            <button
              type="button"
              className="btn btn-link p-0 d-flex align-items-center justify-content-center"
              onClick={() => navigate(backPath)}
              style={{
                fontSize: "2rem",
                lineHeight: 1,
                textDecoration: "none",
                color: "#000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title="Back to Projects"
            >
              <ArrowLeft size={28} style={{ margin: 0 }} />
            </button>
            {isLoading ? (
              <StoreProjectHeaderTitleSkeleton />
            ) : (
              <h5 className="fw-bold mb-0 fs-6">{headerTitle}</h5>
            )}
            {showLoginToViewPrice && (
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none small ms-2"
                onClick={() => {
                  if (typeof openAuthModal === "function") {
                    openAuthModal();
                    return;
                  }
                  window.location.assign("/register");
                }}
              >
                Login to view price
              </button>
            )}
          </div>
        </div>
        <div className="d-flex align-items-center gap-1">
          {showHeaderPriceSkeleton && (
            <div
              className="placeholder-glow py-1 px-1"
              aria-busy="true"
              aria-label="Loading price"
            >
              <StoreProjectHeaderPriceSkeleton />
            </div>
          )}
          {!showHeaderPriceSkeleton &&
            canShowPriceForViewer &&
            headerPayableTotal != null && (
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none justify-content-end m-0 py-1 px-1 small text-nowrap d-flex align-items-center gap-1 flex-wrap store-products-row__price-row"
                onClick={handleHeaderPriceSummaryClick}
                disabled={isPlacingOrder || archivedBlocksPlace}
                aria-label="View price summary"
                title={
                  archivedBlocksPlace
                    ? "Remove archived products to view price summary"
                    : "View price summary"
                }
              >
                <Info size={14} className="text-primary" aria-hidden />
                <span className="text-primary fw-bold fs-6">
                  {formatInr(Math.round(headerPayableTotal))}
                </span>
              </button>
            )}
          <div className="position-relative" ref={projectActionsRef}>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary rounded p-2 d-flex align-items-center justify-content-center"
              style={{ width: "34px", height: "34px", lineHeight: 1 }}
              aria-expanded={showProjectActionsMenu}
              aria-label="Project actions"
              onClick={() => setShowProjectActionsMenu((prev) => !prev)}
            >
              <MoreVertical size={16} aria-hidden />
            </button>
            {showProjectActionsMenu && (
              <div
                className="dropdown-menu store-project-actions-menu show position-absolute top-100 end-0 mt-1 py-1"
                role="menu"
                style={{ minWidth: "170px", zIndex: 1200 }}
              >
                {isDraftProject && !isSampleQuote && (
                  <button
                    type="button"
                    className="dropdown-item rounded-0 text-start px-3 py-2"
                    role="menuitem"
                    onClick={openRenameModal}
                  >
                    Rename
                  </button>
                )}
                {!isSampleQuote && (
                  <button
                    type="button"
                    className="dropdown-item rounded-0 text-start px-3 py-2"
                    role="menuitem"
                    onClick={openDuplicateModal}
                  >
                    Duplicate
                  </button>
                )}
                {isDraftProject && !isSampleQuote && (
                  <button
                    type="button"
                    className="dropdown-item rounded-0 text-start px-3 py-2 text-danger"
                    role="menuitem"
                    onClick={openArchiveModal}
                  >
                    Archive
                  </button>
                )}
                {isDraftProject && (
                  <button
                    type="button"
                    className="dropdown-item rounded-0 text-start px-3 py-2 border-top"
                    style={{ borderTopStyle: "dotted" }}
                    role="menuitem"
                    onClick={downloadEstimate}
                  >
                    Download Estimate
                  </button>
                )}
                {proformaInvoiceEligible && (
                  <button
                    type="button"
                    className={`dropdown-item rounded-0 text-start px-3 py-2`}
                    style={
                      isDraftProject ? undefined : { borderTopStyle: "dotted" }
                    }
                    role="menuitem"
                    onClick={downloadProformaInvoice}
                  >
                    Download Proforma
                  </button>
                )}
                {currentUser?.admin && proformaInvoiceEligible && (
                  <button
                    type="button"
                    className="dropdown-item rounded-0 text-start px-3 py-2 text-danger"
                    role="menuitem"
                    onClick={handleResetProformaInvoice}
                  >
                    Reset PI Number
                  </button>
                )}
                {packingListEligible && (
                  <button
                    type="button"
                    className="dropdown-item rounded-0 text-start px-3 py-2 border-top"
                    style={{ borderTopStyle: "dotted" }}
                    role="menuitem"
                    onClick={downloadPackingList}
                  >
                    Packing List
                  </button>
                )}
                <button
                  type="button"
                  className="dropdown-item rounded-0 text-start px-3 py-2"
                  role="menuitem"
                  onClick={openInstallation}
                >
                  Installation
                </button>
                <button
                  type="button"
                  className="dropdown-item rounded-0 text-start px-3 py-2"
                  role="menuitem"
                  onClick={downloadShutters}
                >
                  Shutter Cutlist
                </button>
                {currentUser?.admin && (
                  <button
                    type="button"
                    className="dropdown-item rounded-0 text-start px-3 py-2"
                    role="menuitem"
                    onClick={downloadArtifacts}
                  >
                    Prod Artifacts
                  </button>
                )}
                {currentUser?.admin && (
                  <button
                    type="button"
                    className="dropdown-item rounded-0 text-start px-3 py-2 border-top"
                    style={{ borderTopStyle: "dotted" }}
                    role="menuitem"
                    onClick={openCopyProjectModal}
                  >
                    Copy Project
                  </button>
                )}
              </div>
            )}
          </div>
          {projectMeta && projectMeta.estimated_price && (
            <div className="fs-5 fw-semibold text-danger">
              ₹{projectMeta.estimated_price}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {!isLoading && isSampleQuote && (
        <Alert style={{ background: "#fdea9f" }}>
          <i className="fa-solid fa-gift text-success" /> Yay! You've got{" "}
          <b>
            <span className="text-success">FREE Delivery</span>
          </b>{" "}
          for this order!
        </Alert>
      )}

      {!isLoading && showStaleQuotePrices && (
        <div
          className="alert alert-warning d-flex flex-wrap align-items-center justify-content-between gap-2"
          role="status"
        >
          <span className="mb-0 fw-bold d-inline-flex align-items-center gap-2">
            <CircleAlert
              size={18}
              className="text-danger flex-shrink-0"
              aria-hidden
            />
            One or more product prices have changed.
          </span>
          <button
            type="button"
            className="btn btn-sm btn-primary text-nowrap"
            onClick={() => handleRefreshQuotePrices()}
            disabled={isRefreshingQuotePrices || isPlacingOrder}
          >
            {isRefreshingQuotePrices ? "Updating…" : "Update prices"}
          </button>
        </div>
      )}

      {!isLoading && projectActionDock && !hideBuyerProjectActionDock && (
        <div
          className="position-fixed start-0 end-0 bottom-0 d-flex flex-column align-items-center border-top bg-white"
          style={{
            zIndex: 1040,
            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 10px)",
          }}
          role="status"
          aria-live="polite"
        >
          {canBackToDraft && projectStatus !== "rejected" && (
            <button
              type="button"
              className="mb-1 btn btn-link btn-sm p-0 text-decoration-none"
              onClick={() => setShowBackToDraftConfirm(true)}
              disabled={isPlacingOrder}
            >
              Back to Quote
            </button>
          )}
          <div
            className="d-flex flex-column gap-3 w-100 px-2 pt-3"
            style={{ maxWidth: "min(680px, calc(100vw - 16px))" }}
          >
            {storeViewerRole === "buyer" && (
              <div className="d-flex justify-content-center align-items-center gap-2">
                <span className="fs-6">Got any queries before ordering?</span>
                <a
                  href={`tel:${SAMPLE_ORDER_SUPPORT_PHONE}`}
                  className="btn fw-semibold border border-primary text-primary d-flex justify-content-center align-items-center"
                >
                  Call us
                </a>
              </div>
            )}
            <span
              id="store-place-order-btn"
              className={`d-flex w-100${
                dockPlaceOrderInteractable ? "" : " pe-none"
              }`}
              onClick={
                dockPlaceOrderInteractable ? handleDockActionClick : undefined
              }
              role={dockPlaceOrderInteractable ? "button" : undefined}
              tabIndex={dockPlaceOrderInteractable ? 0 : undefined}
              onKeyDown={
                dockPlaceOrderInteractable ? handleDockActionKeyDown : undefined
              }
              title={
                !dockPlaceOrderInteractable
                  ? projectActionDock.label
                  : undefined
              }
            >
              <Button
                type="button"
                color="link"
                className={`text-bg-primary text-decoration-none border-0 shadow-none text-white w-100 fw-semibold text-center py-3 rounded-4 fs-6
                ${
                  !dockPlaceOrderInteractable ||
                  isPlacingOrder ||
                  isRefreshingQuotePrices
                    ? dockPlaceOrderInteractable
                      ? "text-white link-light opacity-75"
                      : "text-secondary"
                    : "text-white link-light"
                }`}
                disabled={
                  isPlacingOrder ||
                  isRefreshingQuotePrices ||
                  !dockPlaceOrderInteractable
                }
                onClick={(e) => e.preventDefault()}
              >
                {isPlacingOrder
                  ? "Updating..."
                  : shouldShowViewSummaryButton
                  ? "View Summary"
                  : projectActionDock.label}
              </Button>
            </span>
            {archivedBlocksPlace && (
              <UncontrolledTooltip target="store-place-order-btn">
                This quote includes archived products. Remove or replace them
                before placing the order.
              </UncontrolledTooltip>
            )}
          </div>
        </div>
      )}

      <div className="row g-0" style={{ paddingBottom: "137px" }}>
        <div className="col-12">
          <div
            ref={cartPanelRef}
            className="border rounded-3 bg-white shadow-sm h-100 d-flex flex-column"
          >
            <DndContext
              sensors={sensors}
              collisionDetection={projectCartCollisionDetection}
              onDragStart={handleDndDragStart}
              onDragMove={handleDndDragMove}
              onDragEnd={handleDndDragEnd}
              onDragCancel={handleDndDragCancel}
            >
              <div className="p-0 flex-grow-1 overflow-auto">
                {isDraggingItem && cartReorderDndActive && (
                  <ProjectRootDropZone isDraggingItem={isDraggingItem} />
                )}
                {isLoading ? (
                  <StoreProjectCartLoadingSkeleton rows={7} />
                ) : itemTree.length === 0 ? (
                  <div className="px-2 py-3 text-center text-muted small">
                    {isSampleQuote
                      ? "Sample cabinet quote — items cannot be changed."
                      : canMutateProjectCart
                      ? 'No items added yet. Click "+ Add Folder" to get started.'
                      : "No items in this project yet."}
                  </div>
                ) : (
                  <CartCloneFlashProvider>
                    <ProjectItemTree
                      items={itemTree}
                      projectId={projectId}
                      onRefresh={() => loadItems({ silent: true })}
                      selectedItemId={selectedItemId}
                      onSelectItem={setSelectedItemId}
                      draggingItemId={draggingItemId}
                      isDraggingItem={isDraggingItem}
                      activeDropTargetId={cartReorderActiveDropId}
                      mobileActionsItemId={mobileActionsItemId}
                      onToggleMobileActions={toggleMobileActionsForItem}
                      canEditProjectCart={canEditProjectCart}
                      canMutateProjectCart={canMutateProjectCart}
                      canRemoveProductInCartMode={
                        isDraftProject &&
                        canManageProjectItems &&
                        !isSampleQuote
                      }
                      reorderDropTargetsEnabled={cartReorderDndActive}
                      canShowPrice={canShowPriceForViewer}
                      usePersistedItemPrices={usePersistedItemPrices}
                      canCloneItems={isDraftProject && !isSampleQuote}
                      isSampleQuote={isSampleQuote}
                      onNonCustomerProductDecrement={
                        handleNonCustomerProductDecrement
                      }
                      openFolderIds={openProjectFolderIds}
                      onToggleFolder={toggleProjectFolder}
                      onOpenProductDetail={openProductDetailFromLine}
                      onAuthRequired={openAuthModal}
                      onDuplicateProduct={(item) =>
                        setDuplicateFolderPicker({ isOpen: true, item })
                      }
                      depth={0}
                    />
                    <ProjectCartDuplicateFolderPicker
                      duplicateFolderPicker={duplicateFolderPicker}
                      onClose={() =>
                        setDuplicateFolderPicker({ isOpen: false, item: null })
                      }
                      projectId={projectId}
                      projectName={projectName}
                      onRefresh={() => loadItems({ silent: true })}
                    />
                  </CartCloneFlashProvider>
                )}
              </div>
              <DragOverlay dropAnimation={null}>
                {cartReorderDndActive &&
                  draggingItemId != null &&
                  cartDragOverlaySourceItem && (
                    <div
                      className="d-flex align-items-center gap-2 rounded-2 border shadow-sm bg-white px-3 py-2 small fw-semibold text-dark pe-none"
                      style={{ maxWidth: "min(360px, calc(100vw - 24px))" }}
                    >
                      <i
                        className={`fa-solid ${
                          cartDragOverlaySourceItem.item_type === "folder"
                            ? "fa-folder"
                            : "fa-grip-vertical"
                        } text-secondary`}
                        aria-hidden
                      />
                      <span className="text-truncate">
                        {getProjectItemDisplayName(cartDragOverlaySourceItem)}
                      </span>
                    </div>
                  )}
              </DragOverlay>
            </DndContext>
            {canMutateProjectCart && (
              <div className="px-2 pb-2 pt-2 mt-auto">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => handleAddFolder(null)}
                >
                  + Add Folder
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {!isLoading && isAcceptedProject && projectShipments.length > 0 && (
        <div className="mt-3">
          <h6 className="mb-2 text-muted">Shipments</h6>
          <div className="d-flex flex-column gap-2">
            {projectShipments.map((shipment, index) => (
              <div
                key={`project-shipment-${index}`}
                className="border rounded-2 px-2 py-1 bg-light d-flex align-items-center justify-content-between gap-2"
                role="button"
                tabIndex={0}
                onClick={() => handleOpenShipmentItemsModal(index)}
                onKeyDown={(e) => handleShipmentCardKeyDown(e, index)}
              >
                <span className="fw-semibold">{shipment.name}</span>
                <span className="text-muted text-capitalize">
                  {shipment.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Folder Modal */}
      {showFolderModal && canMutateProjectCart && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 2000,
            alignItems: "flex-start",
          }}
        >
          <div className="w-100" style={{ maxWidth: 480, marginTop: "4rem" }}>
            <div className="card shadow border-0 rounded-3">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0 fw-bold">Add Folder</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => {
                      setShowFolderModal(false);
                      setFolderName("");
                      setFolderParentId(null);
                    }}
                  ></button>
                </div>

                <form onSubmit={handleCreateFolder}>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Folder Name
                    </label>
                    <input
                      type="text"
                      className="form-control unmask"
                      placeholder="e.g. Kitchen, Sink Side, Tall Unit"
                      value={folderName}
                      onChange={(e) => setFolderName(e.target.value)}
                      autoFocus
                    />
                  </div>

                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={() => {
                        setShowFolderModal(false);
                        setFolderName("");
                        setFolderParentId(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary fw-semibold"
                      disabled={!folderName.trim() || isCreatingFolder}
                    >
                      {isCreatingFolder ? "Creating..." : "Add Folder"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={isShipmentItemsModalOpen}
        toggle={() => setIsShipmentItemsModalOpen(false)}
        centered
      >
        <ModalHeader toggle={() => setIsShipmentItemsModalOpen(false)}>
          {selectedShipment?.name}{" "}
          <span className="text-muted text-capitalize">
            ({selectedShipment?.status || "—"})
          </span>
        </ModalHeader>
        <ModalBody>
          <div className="small text-muted mb-2">
            Delivery Date: {formatShipmentDate(selectedShipment?.delivery_date)}
          </div>
          {selectedShipmentItems.length > 0 ? (
            <div className="d-flex flex-column gap-2">
              {renderShipmentTree(
                buildShipmentItemsTree(selectedShipmentItems)
              )}
            </div>
          ) : (
            <div className="text-muted">No shipment items.</div>
          )}
        </ModalBody>
      </Modal>

      <Modal
        isOpen={
          showPlaceOrderConfirm &&
          projectActionDock?.variant === "button" &&
          !shouldShowViewSummaryButton &&
          Boolean(projectNextStatus) &&
          Boolean(projectActionDock?.label)
        }
        toggle={() => !isPlacingOrder && setShowPlaceOrderConfirm(false)}
        centered
      >
        <ModalHeader
          toggle={() => !isPlacingOrder && setShowPlaceOrderConfirm(false)}
        >
          Confirm {projectActionDock?.label}
        </ModalHeader>
        <ModalBody>
          Are you sure you want to <strong>{projectActionDock?.label}</strong>?
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowPlaceOrderConfirm(false)}
            disabled={isPlacingOrder}
          >
            Cancel
          </Button>
          <Button
            color="success"
            onClick={() => handlePlaceOrder()}
            disabled={isPlacingOrder}
          >
            {isPlacingOrder ? "Updating..." : projectActionDock?.label}
          </Button>
        </ModalFooter>
      </Modal>

      <StoreProjectPriceSummary
        isOpen={isPriceSummaryOpen}
        projectId={projectId}
        canManageProjectItems={canManageProjectItems}
        isBusy={isPlacingOrder || dealerAcceptingOrder}
        hidePricing={storeViewerRole === "dealer"}
        dealerOrderRow={dealerOrder}
        onDealerReviewOrder={openDealerReviewModal}
        acceptingDealerOrder={dealerAcceptingOrder}
        onClose={() => {
          if (!isPlacingOrder && !dealerAcceptingOrder) {
            setIsPriceSummaryOpen(false);
            void refreshHeaderPayableTotal();
          }
        }}
        onPlaceOrder={handlePlaceOrder}
        onBeginPlaceOrder={beginPlaceOrderFlow}
        externalPlaceOrderConfirmMode={pendingPlaceOrderConfirmMode}
        onExternalPlaceOrderConfirmHandled={() =>
          setPendingPlaceOrderConfirmMode(null)
        }
        onHeaderPayableFromSummary={handleSummaryPayableHeaderSync}
        onQuotePricesRefreshed={() => loadItems({ silent: true })}
        onIncludesUpdated={() => loadItems({ silent: true })}
        isSampleQuote={isSampleQuote}
        items={items}
      />

      <StoreProjectNameModal
        isOpen={placeOrderNameModal.isOpen}
        toggle={closePlaceOrderNameModal}
        title="Project Name"
        value={placeOrderNameModal.draftName}
        onChange={(value) =>
          setPlaceOrderNameModal((m) => ({
            ...m,
            draftName: value,
            error: null,
          }))
        }
        onSubmit={handlePlaceOrderNameSubmit}
        busy={placeOrderNameModal.saving}
        error={placeOrderNameModal.error}
        submitLabel={
          placeOrderNameModal.saving ? "Saving..." : "Continue to place order"
        }
      />

      <DealerOrderReviewModal
        isOpen={dealerReviewModal.isOpen}
        projectId={dealerOrder?.id ?? projectId}
        projectName={dealerOrder?.name ?? projectName}
        meta={dealerOrder?.dealer_price_meta ?? dealerOrder?.meta ?? null}
        priceMeta={dealerOrder?.price_meta ?? null}
        buyerPayable={dealerOrder?.buyer_payable_total ?? null}
        dealerPayable={dealerOrder?.payable_total ?? null}
        margin={dealerOrder?.margin ?? null}
        creditAdjustmentsTotal={dealerOrder?.credit_adjustments_total ?? 0}
        showAcceptButton={dealerReviewModal.showAcceptButton}
        onClose={closeDealerReviewModal}
        onAccept={handleDealerAcceptOrder}
        accepting={dealerAcceptingOrder}
      />

      <Modal
        isOpen={showBackToDraftConfirm}
        toggle={() => !isPlacingOrder && setShowBackToDraftConfirm(false)}
        centered
      >
        <ModalHeader
          toggle={() => !isPlacingOrder && setShowBackToDraftConfirm(false)}
        >
          Confirm Back to Quote
        </ModalHeader>
        <ModalBody>
          Are you sure you want to move this project back to{" "}
          <strong>Quote</strong>?
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowBackToDraftConfirm(false)}
            disabled={isPlacingOrder}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleBackToDraft}
            disabled={isPlacingOrder}
          >
            {isPlacingOrder ? "Updating..." : "Confirm"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={renameModal.isOpen} toggle={closeRenameModal} centered>
        <form onSubmit={handleRenameSubmit}>
          <ModalHeader toggle={closeRenameModal}>Rename Quote</ModalHeader>
          <ModalBody>
            {renameModal.error && (
              <div className="alert alert-danger py-2 px-3 mb-3" role="alert">
                {renameModal.error}
              </div>
            )}
            <Label
              for="rename-quote-name-input"
              className="form-label fw-semibold"
            >
              Name
            </Label>
            <Input
              id="rename-quote-name-input"
              type="text"
              className="unmask"
              value={renameModal.draftName}
              onChange={(e) =>
                setRenameModal((m) => ({ ...m, draftName: e.target.value }))
              }
              autoFocus
            />
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              type="button"
              onClick={closeRenameModal}
              disabled={renameModal.saving}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              className="fw-semibold"
              disabled={!renameModal.draftName.trim() || renameModal.saving}
            >
              {renameModal.saving ? "Saving..." : "Save"}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      <Modal
        isOpen={duplicateModal.isOpen}
        toggle={closeDuplicateModal}
        centered
      >
        <ModalHeader toggle={closeDuplicateModal}>Duplicate Quote</ModalHeader>
        <ModalBody>
          {duplicateModal.error && (
            <div className="alert alert-danger py-2 px-3 mb-3" role="alert">
              {duplicateModal.error}
            </div>
          )}
          <p className="mb-0">Create a draft copy of this quote?</p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            type="button"
            onClick={closeDuplicateModal}
            disabled={duplicateModal.saving}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            type="button"
            className="fw-semibold"
            onClick={handleDuplicateConfirm}
            disabled={duplicateModal.saving}
          >
            {duplicateModal.saving ? "Duplicating..." : "Duplicate"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={copyProjectModal.isOpen}
        toggle={closeCopyProjectModal}
        centered
        size="md"
      >
        <ModalHeader toggle={closeCopyProjectModal}>Copy Project</ModalHeader>
        <ModalBody>
          {copyProjectModal.error && (
            <div className="alert alert-danger py-2 px-3 mb-3" role="alert">
              {copyProjectModal.error}
            </div>
          )}
          <Label for="copy-project-mobile" className="fw-semibold">
            Mobile number
          </Label>
          <div className="d-flex gap-2 mb-3">
            <Input
              id="copy-project-mobile"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="10-digit mobile"
              value={copyProjectModal.mobile}
              onChange={handleCopyProjectMobileChange}
              disabled={copyProjectModal.searching || copyProjectModal.saving}
            />
            <Button
              color="primary"
              type="button"
              className="flex-shrink-0"
              onClick={handleCopyProjectSearch}
              disabled={
                !/^\d{10}$/.test(copyProjectModal.mobile) ||
                copyProjectModal.searching ||
                copyProjectModal.saving
              }
            >
              {copyProjectModal.searching ? "Searching..." : "Search"}
            </Button>
          </div>
          {copyProjectModal.searchError && (
            <div className="text-muted small mb-3">
              {copyProjectModal.searchError}
            </div>
          )}

          {copyProjectModal.results.length > 0 && (
            <div className="d-flex flex-column gap-2">
              {copyProjectModal.results.map((result) => {
                const row = buyerUserSearchResultToRow(result);
                const isSelected =
                  copyProjectModal.selectedBuyerUserId === result.id;

                return (
                  <button
                    key={result.id}
                    type="button"
                    className={`btn text-start w-100 p-3 border rounded ${
                      isSelected
                        ? "border-primary bg-light"
                        : "border-secondary-subtle"
                    }`}
                    onClick={() =>
                      setCopyProjectModal((m) => ({
                        ...m,
                        selectedBuyerUserId: result.id,
                        error: null,
                      }))
                    }
                    disabled={copyProjectModal.saving}
                  >
                    <BuyerDashboardNameCell row={row} />
                  </button>
                );
              })}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            type="button"
            onClick={closeCopyProjectModal}
            disabled={copyProjectModal.saving}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            type="button"
            className="fw-semibold"
            onClick={handleCopyProjectConfirm}
            disabled={
              copyProjectModal.selectedBuyerUserId == null ||
              copyProjectModal.saving
            }
          >
            {copyProjectModal.saving ? "Copying..." : "Copy Project"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={archiveModal.isOpen} toggle={closeArchiveModal} centered>
        <ModalHeader toggle={closeArchiveModal}>Archive Quote</ModalHeader>
        <ModalBody>
          {archiveModal.error && (
            <div className="alert alert-danger py-2 px-3 mb-3" role="alert">
              {archiveModal.error}
            </div>
          )}
          <p className="mb-0">
            Are you sure you want to archive
            <span className="fw-semibold"> {projectName}</span>? It will no
            longer appear in your quotes list.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            type="button"
            onClick={closeArchiveModal}
            disabled={archiveModal.saving}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            type="button"
            className="fw-semibold"
            onClick={handleArchiveConfirm}
            disabled={archiveModal.saving}
          >
            {archiveModal.saving ? "Archiving..." : "Archive"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={billingDetailsModalOpen}
        toggle={() => setBillingDetailsModalOpen(false)}
        centered
      >
        <ModalHeader toggle={() => setBillingDetailsModalOpen(false)}>
          Billing details required
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">
            Billing details are missing. Complete GST number, billing address,
            and pincode before downloading an estimate or proforma invoice.
          </p>
          {storeViewerRole === "buyer" ? (
            <p className="mb-0 mt-3">
              <button
                type="button"
                className="btn btn-link p-0"
                onClick={() => {
                  setBillingDetailsModalOpen(false);
                  openProfileModal?.();
                }}
              >
                Complete billing details in your profile
              </button>
            </p>
          ) : (
            <p className="mb-0 mt-3 text-muted small">
              Ask the buyer to complete billing details in their profile.
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            onClick={() => setBillingDetailsModalOpen(false)}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={showPlacedWithoutDealerInfo}
        toggle={() => setShowPlacedWithoutDealerInfo(false)}
        centered
      >
        <ModalHeader toggle={() => setShowPlacedWithoutDealerInfo(false)}>
          Order placed
        </ModalHeader>
        <ModalBody>
          <p className="mb-0 small" style={{ whiteSpace: "pre-line" }}>
            Your order was placed directly with ZeroBuild.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() => setShowPlacedWithoutDealerInfo(false)}
          >
            OK
          </Button>
        </ModalFooter>
      </Modal>

      {productDetailModal && (
        <ProductDetailModal
          key={productDetailModal.openKey}
          productId={productDetailModal.productId}
          isOpen
          onClose={() => setProductDetailModal(null)}
          onProductIdChange={(pid) => {
            if (pid == null) return;
            setProductDetailModal((prev) =>
              prev ? { ...prev, productId: String(pid) } : prev
            );
          }}
          nonCartInitialAdded={productDetailModal.initialAdded}
          nonCartInitialHasProjects={productDetailModal.initialHasProjects}
          nonCartInitialFolderPath={productDetailModal.initialFolderPath}
        />
      )}
    </div>
  );
};

export default StoreProjectDetail;
